import _sortBy from 'lodash/sortBy'

import OpportunityService from '@/services/OpportunityService'

const createOpportunitiesModule = () => ({
  namespaced: true,
  state: {
    ready: false,
    dirty: false,
    viewRows: false,
    readyCb: [],
    page: 1,
    pages: 0,
    pageSize: 14,
    total: -1,
    options: {},
    opportunities: [],
    countries: []
  },
  actions: {
    getOpportunities: async ({ commit, state }) => {
      if (!state.dirty) {
        return
      }
      const data = await OpportunityService.getOpportunities(
        state.pageSize,
        state.page,
        state.options
      )

      // const opportunities = sortOpportunities([...data.opportunities])
      if (!data.opportunities) {
        return
      }
      const opportunities = [...data.opportunities]
      commit('SET_OPPORTUNITIES', { opportunities })
      commit('SET_READY', { readyState: true })
      commit('SET_PAGES', { pages: data.pages })
      commit('SET_TOTAL', { total: data.total })
      commit('SET_DIRTY', { dirty: false })
      state.readyCb.forEach((cb) => cb())
      commit('CLEAR_READY_CB')
    },
    getOpportunityLocations: async ({ commit }) => {
      const locations = await OpportunityService.getOpportunityLocations()

      commit('SET_LOCATIONS', { countries: locations })
    },
    addReadyCb: ({ commit }, cb) => {
      commit('ADD_READY_CB', { cb })
    },
    setPopulate: async ({ dispatch, commit }, populate) => {
      commit('SET_OPTION', { key: 'populate', value: populate })
      await dispatch('getOpportunities')
    },
    setPage: async ({ dispatch, commit, state }, { page }) => {
      if (state.page !== page) {
        commit('SET_PAGE', { page })
        commit('SET_DIRTY', { dirty: true })
      }
      await dispatch('getOpportunities')
    },
    setDirty: ({ commit }) => {
      commit('SET_DIRTY', { dirty: true })
    },
    setPageSize: ({ commit, state }, { pageSize }) => {
      if (state.pageSize !== pageSize) {
        commit('SET_DIRTY', { dirty: true })
        commit('SET_PAGE_SIZE', { pageSize })
      }
    },
    setOption: ({ dispatch, commit, state }, { key, value }) => {
      if (state.options['key'] !== value) {
        commit('SET_PAGE', { page: 1 })
        commit('SET_DIRTY', { dirty: true })
        commit('SET_OPTION', { key, value })
        dispatch('getOpportunities')
      }
    },
    setOptionOnly: ({ commit, state }, { key, value }) => {
      if (state.options[key] !== value) {
        commit('SET_OPTION', { key, value })
        commit('SET_DIRTY', true)
      }
    },
    resetOptions: ({ commit }) => {
      commit('RESET_OPTIONS')
    },
    setViewRows: ({ commit }, viewRows) => {
      commit('SET_VIEW_ROWS', viewRows)
    }
  },
  getters: {
    ready: (state) => state.ready,
    opportunities: (state, getters, rootState, rootGetters) => {
      let currentOpportunities = state.opportunities

      const { opportunityType } = rootState.route.params

      const allOpportunityTypes = rootGetters['opportunityTypes/opportunityTypes']

      if (opportunityType && allOpportunityTypes && allOpportunityTypes.length) {
        const opportunityParentType = allOpportunityTypes.find(
          (o) =>
            o.slug === opportunityType ||
            o.slugPlural === opportunityType ||
            o.slugAlternative === opportunityType ||
            o.slugAlternativePlural === opportunityType
        )

        if (!opportunityParentType) {
          return []
        }

        const opportunityTypes = allOpportunityTypes
          .filter(
            (o) =>
              o.parent &&
              (o.parent.slug === opportunityType ||
                o.parent.slugPlural === opportunityType ||
                o.parent.slugAlternative === opportunityType ||
                o.parent.slugAlternativePlural === opportunityType)
          )
          .map((o) => o.slug)

        opportunityTypes.push(opportunityParentType.slug)
        currentOpportunities = currentOpportunities.filter((o) =>
          opportunityTypes.includes(o.opportunityType.slug)
        )
      }

      if (opportunityType === 'data-science-events') {
        return _sortBy(currentOpportunities, ['eventDate'], ['desc'])
      }

      return currentOpportunities
    },
    opportunitiesByFilter: (state) => {
      return (filter) => state.opportunities.filter(filter)
    },
    filteredOpportunities: (state, getters, rootState, rootGetters) => {
      let currentOpportunities = getters.opportunities

      const filterOptions = [
        { model: 'activeDomain', property: 'domain', by: '_id' },
        { model: 'activeType', property: 'opportunityType', by: '_id' },
        { model: 'activeCompany', property: 'company', by: '_id' },
        { model: 'activeLevel', property: 'skillLevel', by: '_id' },
        { model: 'activeCountryCode', property: 'country' },
        { model: 'activeCity', property: 'city' },
        { model: 'activeSkill', property: 'skills', propertyType: 'array' }
      ]

      filterOptions.forEach((filterOption) => {
        const filterValue = rootGetters[`opportunityFilter/${filterOption.model}`]
        if (filterValue && filterValue !== '') {
          const property = filterOption.property

          currentOpportunities = currentOpportunities.filter((o) => o[property])
          let filter = (o) => o[property] === filterValue

          if (filterOption.by) {
            filter = (o) => o[property][filterOption.by] === filterValue[filterOption.by]
          } else if (filterOption.propertyType === 'array') {
            filter = (o) => o[property].includes(filterValue)
          }
          currentOpportunities = currentOpportunities.filter(filter)
        }
      })

      return currentOpportunities
    },
    countries: (state) => state.countries,
    page: (state) => state.page,
    pages: (state) => state.pages,
    pageSize: (state) => state.pageSize,
    total: (state) => state.total,
    prevDisabled: (state) => state.page === 1,
    nextDisabled(state) {
      return state.pageSize * state.page >= state.total
    },
    options: (state) => state.options,
    viewRows: (state) => state.viewRows
  },
  mutations: {
    SET_READY: (state, { readyState }) => {
      state.ready = readyState
    },
    ADD_READY_CB: (state, { cb }) => {
      state.readyCb.push(cb)
    },
    CLEAR_READY_CB: (state) => {
      state.readyCb = []
    },
    SET_OPPORTUNITIES: (state, { opportunities }) => {
      state.opportunities = opportunities
    },
    SET_LOCATIONS: (state, { locations }) => {
      state.locations = locations
    },
    SET_PAGES: (state, { pages }) => {
      state.pages = pages
    },
    SET_PAGE: (state, { page }) => {
      state.page = page
    },
    SET_PAGE_SIZE: (state, { pageSize }) => {
      state.pageSize = pageSize
    },
    SET_OPTION: (state, { key, value }) => {
      if (state.options[key] !== value) {
        state.options[key] = value
        state.dirty = true
      }
    },
    SET_DIRTY: (state, { dirty }) => {
      state.dirty = dirty
    },
    RESET_OPTIONS: (state) => {
      state.options = {}
    },
    SET_TOTAL: (state, { total }) => {
      state.total = total
    },
    SET_VIEW_ROWS: (state, viewRows) => {
      state.viewRows = viewRows
    }
  }
})

export default createOpportunitiesModule
